import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, CalendarCheck, ClipboardCheck, Plus, Scale } from 'lucide-react'
import { getAccountingSummary } from '../../api/accounting'
import Button from '../../components/Button'
import { Card, Tile } from '../../components/Summary'
import { StatusBadge } from '../../components/PageParts'
import { HelpTip } from '../../components/HelpTip'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money } from '../sales/salesShared'
import AccountingTabs from './AccountingTabs'
import { STATUS_TONE, monthName } from './accountingShared'

const REPORTS = [
  { to: '/accounting/reports/income-statement', label: 'Income statement', k: 'incomeStatement' },
  { to: '/accounting/reports/balance-sheet', label: 'Balance sheet', k: 'balanceSheet' },
  { to: '/accounting/reports/trial-balance', label: 'Trial balance', k: 'trialBalance' },
  { to: '/accounting/reports/general-ledger', label: 'General ledger', k: 'generalLedger' },
  { to: '/accounting/reports/ap-subledger', label: 'AP subledger', k: 'apSubledger' },
  { to: '/accounting/reports/ar-subledger', label: 'AR subledger', k: 'arSubledger' },
]

// The Accounting summary: the state of this month and last, the books'
// balance, what is waiting on Accounting, and the latest entries.
export default function AccountingMenu() {
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    getAccountingSummary().then(setData).catch(() => setFailed(true))
  }, [])

  return (
    <div className="section-page">
      <AccountingTabs />
      <header className="section-header">
        <h1>Accounting</h1>
        <div className="section-actions">
          <Button as={Link} to="/accounting/reports" variant="ghost" size="sm">Reports</Button>
          <Button data-write as={Link} to="/accounting/journal/new" variant="primary" size="sm"><Plus size={14} />New journal entry</Button>
        </div>
      </header>
      {data ? <Summary data={data} /> : <p className="sum-state">{failed ? 'The summary could not be loaded.' : 'Loading accounting…'}</p>}
    </div>
  )
}

function Summary({ data }) {
  const month = monthName(data.period_ym, { year: false })
  const lastMonth = monthName(data.last_ym, { year: false })
  const inv = data.invoices || {}
  const invWaiting = (inv.to_check || 0) + (inv.to_approve || 0) + (inv.to_post || 0)
  const diff = Number(data.books?.difference || 0)
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

  const todo = [
    data.drafts > 0 && { to: '/accounting/journal-entries?show=drafts', text: `${plural(data.drafts, 'draft journal entry', 'draft journal entries')} to post or delete`, k: 'draft' },
    inv.to_check > 0 && { to: '/accounting/ap/matching?show=check', text: `${plural(inv.to_check, 'vendor invoice', 'vendor invoices')} to check against the order and receipt` },
    inv.to_approve > 0 && { to: '/accounting/ap/matching?show=approve', text: `${plural(inv.to_approve, 'matched vendor invoice', 'matched vendor invoices')} to approve` },
    inv.to_post > 0 && { to: '/accounting/ap/matching?show=post', text: `${plural(inv.to_post, 'approved invoice', 'approved invoices')} to post to accounts payable`, k: 'accountsPayable' },
    data.depreciation_due > 0 && { to: '/accounting/assets', text: `Depreciation due on ${plural(data.depreciation_due, 'asset', 'assets')}`, k: 'depreciation' },
    data.prepaids_due > 0 && { to: '/accounting/prepaids', text: `${plural(data.prepaids_due, 'prepaid', 'prepaids')} to amortize`, k: 'prepaid' },
    data.bank?.unmatched > 0 && { to: '/accounting/bank-rec', text: `${plural(data.bank.unmatched, 'bank statement line', 'bank statement lines')} to match`, k: 'bankRec' },
    !data.last_closed && { to: `/accounting/month-end?month=${data.last_ym}`, text: `Close ${lastMonth}`, k: 'periodClose' },
  ].filter(Boolean)

  return (
    <>
      <div className="sum-tiles">
        <Tile label={`${month} books`} href="/accounting/month-end" icon={CalendarCheck}
          value={data.period_closed ? 'Closed' : 'Open'}
          note={data.last_closed ? `${lastMonth} is closed` : `${lastMonth} is still open`}
          tone={data.last_closed ? undefined : 'warn'} />
        <Tile label={`Journal entries in ${month}`} href="/accounting/journal-entries" icon={BookOpen}
          value={String(data.entries_this_month)}
          note={data.drafts > 0 ? `${plural(data.drafts, 'draft', 'drafts')} waiting` : `${data.manual_this_month} made by hand`} />
        <Tile label="Vendor invoices waiting" href="/accounting/ap/matching" icon={ClipboardCheck}
          value={String(invWaiting)}
          note={invWaiting ? [inv.to_check && `${inv.to_check} to check`, inv.to_approve && `${inv.to_approve} to approve`, inv.to_post && `${inv.to_post} to post`].filter(Boolean).join(' · ') : 'Nothing to check'}
          tone={invWaiting ? 'warn' : undefined} />
        <Tile label="Trial balance" href="/accounting/reports/trial-balance" icon={Scale}
          value={Math.abs(diff) < 0.005 ? 'Balanced' : `Out by ${money(Math.abs(diff))}`}
          note={`Debits ${money(data.books?.debits)} · credits ${money(data.books?.credits)}`}
          tone={Math.abs(diff) < 0.005 ? undefined : 'warn'} />
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          <Card title="To do">
            {todo.length ? (
              <ul className="sum-list acc-todo">
                {todo.map((t, i) => (
                  <li key={i}>
                    <span className="sum-list-main">
                      <Link to={t.to}>{t.text}</Link>
                      {t.k && <HelpTip k={t.k} />}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">Nothing waiting. The books are up to date.</p>
            )}
          </Card>

          <Card title="Latest journal entries" action={<Link to="/accounting/journal-entries">All entries</Link>}>
            {data.recent?.length ? (
              <ul className="sum-list">
                {data.recent.map((r) => (
                  <li key={r.je_id}>
                    <span className="sum-list-main">
                      <Link to={`/accounting/journal/${r.je_id}`}>{docNo('je', r.je_no)}</Link>
                      {' '}{r.memo || <span className="sum-muted">No description</span>}
                      <span className="sum-muted"> · {r.source.label} · {fmtDay(r.date, { year: false })}</span>
                      {r.status !== 'Posted' && <> <StatusBadge label={r.status} tone={STATUS_TONE[r.status]} /></>}
                    </span>
                    <span className="sum-list-side">{money(r.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">No journal entries yet.</p>
            )}
          </Card>
        </div>

        <div className="sum-side">
          <Card title="Reports" action={<Link to="/accounting/reports">All reports</Link>}>
            <ul className="sum-list acc-reports">
              {REPORTS.map((r) => (
                <li key={r.to}>
                  <span className="sum-list-main">
                    <Link to={r.to}>{r.label}</Link>
                    <HelpTip k={r.k} />
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  )
}
